import { Clear } from "@mui/icons-material";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography
} from "@mui/material";
import { GlobalAdmin as SdkGlobalAdmin } from "coolremote-sdk";
import { Formik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { t } from "ttag";
import * as Yup from "yup";
import { useStoreActions, useStoreState } from "../../../models/RootStore";
import CustomerSection from "./CustomerSection";
import SiteSetupFields from "./SiteSetupFields";
import { getEmptySiteSetupFields, siteSetupFieldsShape, toSiteSetupPayload } from "./siteSetupForm";
import useStyles from "./SiteSetup.style";

const formId = "site-setup-form";

const validationSchema = Yup.object().shape({
  ...siteSetupFieldsShape,
  customerMode: Yup.string().oneOf(["existing", "new"]),
  customerId: Yup.string().when("customerMode", {
    is: "existing",
    then: Yup.string().required(t`Please select a customer`)
  }),
  newCustomerName: Yup.string().when("customerMode", {
    is: "new",
    then: Yup.string()
      .trim()
      .required(t`Customer name is required`)
      .max(50, t`Max 50 characters`)
  })
});

const errorText = (err: any) => err?.message || t`Something went wrong`;

// Add New Site flow: creates the customer/user as needed, then the site.
// onCreated receives { site, customer, customerAdminUser }.
const SiteSetupDialog = ({ open, onClose, onCreated, defaultCustomerId }: any) => {
  const classes: any = useStyles();
  const customers = useStoreState((state) => state.customers.customersBasicList);
  const allSites = useStoreState((state) => state.sites.allSites);
  const addCustomer = useStoreActions((actions) => actions.customers.addCustomer);
  const setAllSites = useStoreActions((actions) => actions.sites.setAllSites);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    if (open) {
      setSubmitError("");
    }
  }, [open]);

  // Rebuilt on every open so the form starts clean and default dates count from now
  const initialValues: any = useMemo(() => ({
    customerMode: "existing",
    customerId: defaultCustomerId && customers[defaultCustomerId] ? defaultCustomerId : "",
    newCustomerName: "",
    createCustomerAdminUser: true,
    ...getEmptySiteSetupFields()
  }), [open]);

  const handleErrorClose = () => {
    setSubmitError("");
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  // One backend call creates the customer, its admin user and the site in a single
  // transaction, so a failure leaves nothing half created and the form can simply be resubmitted
  const handleSubmit = async (values: any, formik: any) => {
    setSubmitting(true);
    setSubmitError("");

    const isNewCustomer = values.customerMode === "new";
    const customerPart = isNewCustomer
      ? { newCustomer: { name: values.newCustomerName.trim() }, createCustomerAdminUser: values.createCustomerAdminUser }
      : { customer: values.customerId };

    try {
      const result: any = await SdkGlobalAdmin.setupSite({
        ...customerPart,
        site: toSiteSetupPayload(values, initialValues)
      });

      if (result.customer.isNew) {
        addCustomer({ id: result.customer.id, name: result.customer.name } as any);
      }
      setAllSites({ ...allSites, [result.site.id]: result.site });
      onCreated(result);
    } catch (err) {
      setSubmitError(errorText(err));
    } finally {
      setSubmitting(false);
      formik.setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="site-setup-dialog-title"
      fullWidth
      maxWidth="lg"
      // Header and actions stay put; only the content scrolls once the dialog hits max height
      scroll="paper"
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle
        id="site-setup-dialog-title"
        className={classes.dialogTitle}
      >
        <Typography variant="h6" component="span">{t`Add New Site`}</Typography>
        <IconButton onClick={handleClose} disabled={submitting}>
          <Clear />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        {submitError && (
          <Alert severity="error" className={classes.errorAlert} onClose={handleErrorClose}>
            {submitError}
          </Alert>
        )}
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {(formik) => (
            <form id={formId} onSubmit={formik.handleSubmit}>
              <CustomerSection />

              <Divider className={classes.sectionDivider} />

              <Typography variant="subtitle1" gutterBottom className={classes.mainSectionTitle}>{t`Site`}</Typography>
              <SiteSetupFields />
            </form>
          )}
        </Formik>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting} className={classes.actionButton}>{t`Cancel`}</Button>
        <Button
          variant="contained"
          className={classes.actionButton}
          type="submit"
          form={formId}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {submitting ? t`Creating...` : t`Create`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SiteSetupDialog;
export { default as SiteSetupResultDialog } from "./SiteSetupResultDialog";
export { default as useSiteSetupStyles } from "./SiteSetup.style";
